import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { FaUser, FaGraduationCap, FaFileAlt, FaClock, FaCheckCircle, FaTimesCircle, FaSignOutAlt } from 'react-icons/fa';
import apiService from '../services/api';
import Loading from '../components/Loading';

const StudentDashboard = () => {
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('applications');

  useEffect(() => {
    verifyAuth();
  }, []);

  const verifyAuth = async () => {
    const token = localStorage.getItem('student_token');
    if (!token) {
      navigate('/student/login');
      return;
    }

    try {
      const [authResponse, applicationsResponse] = await Promise.all([
        apiService.verifyStudentToken(),
        apiService.getMyApplications()
      ]);

      if (authResponse.data.success) {
        setStudent(authResponse.data.student);
      }

      if (applicationsResponse.data.success) {
        setApplications(applicationsResponse.data.data);
      }
    } catch (error) {
      console.error('Auth verification failed:', error);
      localStorage.removeItem('student_token');
      localStorage.removeItem('student');
      navigate('/student/login');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await apiService.studentLogout();
      localStorage.removeItem('student_token');
      localStorage.removeItem('student');
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-yellow-100 text-yellow-800', text: 'Pending', icon: FaClock },
      under_review: { color: 'bg-blue-100 text-blue-800', text: 'Under Review', icon: FaFileAlt },
      approved: { color: 'bg-green-100 text-green-800', text: 'Approved', icon: FaCheckCircle },
      rejected: { color: 'bg-red-100 text-red-800', text: 'Rejected', icon: FaTimesCircle },
      waitlisted: { color: 'bg-purple-100 text-purple-800', text: 'Waitlisted', icon: FaClock },
      withdrawn: { color: 'bg-gray-100 text-gray-800', text: 'Withdrawn', icon: FaTimesCircle }
    };

    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${badge.color}`}>
        <Icon className="mr-1" />
        {badge.text}
      </span>
    );
  };

  const getPaymentStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-yellow-100 text-yellow-800', text: 'Payment Pending' },
      verified: { color: 'bg-green-100 text-green-800', text: 'Payment Verified' },
      failed: { color: 'bg-red-100 text-red-800', text: 'Payment Failed' }
    };

    const badge = badges[status] || badges.pending;

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.text}
      </span>
    );
  };

  if (loading) return <Loading message="Loading dashboard..." />;

  return (
    <>
      <Helmet>
        <title>Student Dashboard - EduConsult</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 pt-20">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-secondary-600 text-white py-12">
          <div className="container-custom">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold mb-2">Welcome back, {student?.name}!</h1>
                <p className="text-white/80">{student?.email}</p>
              </div>
              <button
                onClick={handleLogout}
                className="btn bg-white/20 hover:bg-white/30 border-white/30 flex items-center"
              >
                <FaSignOutAlt className="mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>

        <div className="container-custom py-8">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FaUser className="text-primary-600 text-3xl" />
                  </div>
                  <h3 className="font-bold text-gray-900">{student?.name}</h3>
                  <p className="text-sm text-gray-600">{student?.email}</p>
                </div>

                <nav className="space-y-2">
                  <button
                    onClick={() => setActiveTab('applications')}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                      activeTab === 'applications'
                        ? 'bg-primary-50 text-primary-600 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <FaFileAlt className="inline mr-3" />
                    My Applications
                  </button>
                  <Link
                    to="/colleges"
                    className="block w-full text-left px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <FaGraduationCap className="inline mr-3" />
                    Browse Colleges
                  </Link>
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              {/* Stats Cards */}
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white rounded-xl shadow-md p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-600 text-sm">Total Applications</p>
                      <p className="text-3xl font-bold text-gray-900">{applications.length}</p>
                    </div>
                    <div className="bg-primary-100 p-4 rounded-lg">
                      <FaFileAlt className="text-primary-600 text-2xl" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-md p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-600 text-sm">Approved</p>
                      <p className="text-3xl font-bold text-green-600">
                        {applications.filter(app => app.status === 'approved').length}
                      </p>
                    </div>
                    <div className="bg-green-100 p-4 rounded-lg">
                      <FaCheckCircle className="text-green-600 text-2xl" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-md p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-600 text-sm">Under Review</p>
                      <p className="text-3xl font-bold text-blue-600">
                        {applications.filter(app => app.status === 'under_review').length}
                      </p>
                    </div>
                    <div className="bg-blue-100 p-4 rounded-lg">
                      <FaClock className="text-blue-600 text-2xl" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Applications List */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">My Applications</h2>
                  <Link
                    to="/colleges"
                    className="btn btn-primary"
                  >
                    Apply to More Colleges
                  </Link>
                </div>

                {applications.length > 0 ? (
                  <div className="space-y-4">
                    {applications.map((application) => (
                      <div
                        key={application.id}
                        className="border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow"
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-gray-900 mb-1">
                              {application.college_name}
                            </h3>
                            <p className="text-gray-600 mb-2">{application.location}</p>
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span>📚 {application.course_name}</span>
                              <span>🎓 {application.degree_type}</span>
                              <span>🆔 {application.application_number}</span>
                            </div>
                          </div>
                          <div className="text-right">
                            {getStatusBadge(application.status)}
                          </div>
                        </div>

                        <div className="grid md:grid-cols-3 gap-4 pt-4 border-t">
                          <div>
                            <p className="text-xs text-gray-500">Applied Date</p>
                            <p className="font-medium">
                              {new Date(application.applied_date).toLocaleDateString()}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Payment Status</p>
                            {getPaymentStatusBadge(application.payment_status)}
                          </div>
                          <div className="text-right">
                            <Link
                              to={`/student/application/${application.id}`}
                              className="text-primary-600 hover:text-primary-700 font-medium text-sm"
                            >
                              View Details →
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FaFileAlt className="text-gray-300 text-6xl mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      No Applications Yet
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Start your college journey by applying to your dream colleges
                    </p>
                    <Link to="/colleges" className="btn btn-primary">
                      Browse Colleges
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StudentDashboard;
